/**
 * Utilitários para detecção e gerenciamento de domínios
 * Suporta:
 * - imobpanda.com (marketing/landing page)
 * - app.imobpanda.com (login e admin)
 * - *.imobpanda.site (tenants - sites dos usuários)
 */

const MAIN_DOMAIN = 'imobpanda.com';
const TENANT_DOMAIN = 'imobpanda.site';
const APP_SUBDOMAIN = 'app';

/**
 * Verifica se está em ambiente de desenvolvimento (localhost)
 */
export const isLocalhost = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' ||
    hostname.startsWith('localhost:') ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('127.0.0.1:');
};

/**
 * Verifica se está no domínio principal (marketing/landing page)
 */
export const isMainDomain = (): boolean => {
  const hostname = window.location.hostname;
  const cleanHost = hostname.replace(/^www\./, '');

  // localhost sem subdomínio ou domínio principal
  return cleanHost === MAIN_DOMAIN ||
    cleanHost === 'localhost' ||
    cleanHost === '127.0.0.1' ||
    cleanHost.startsWith('localhost:') ||
    cleanHost.startsWith('127.0.0.1:');
};

/**
 * Verifica se está no subdomínio app (login/admin)
 */
export const isAppSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  const cleanHost = hostname.replace(/^www\./, '');

  // app.imobpanda.com ou app.localhost ou app.127.0.0.1
  return cleanHost === `${APP_SUBDOMAIN}.${MAIN_DOMAIN}` ||
    cleanHost === `${APP_SUBDOMAIN}.localhost` ||
    cleanHost.startsWith(`${APP_SUBDOMAIN}.localhost:`) ||
    cleanHost === `${APP_SUBDOMAIN}.127.0.0.1` ||
    cleanHost.startsWith(`${APP_SUBDOMAIN}.127.0.0.1:`);
};

/**
 * Verifica se é um subdomínio de tenant (não app, não www, não main)
 */
export const isTenantSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  const cleanHost = hostname.replace(/^www\./, '');

  // IMPORTANTE: Verificar se é app PRIMEIRO
  if (isAppSubdomain()) {
    return false;
  }

  // Não é domínio principal
  if (cleanHost === MAIN_DOMAIN || isMainDomain()) {
    return false;
  }

  // Em localhost, verificar se tem subdomínio que não seja "app"
  if (isLocalhost()) {
    const parts = cleanHost.split('.');
    if (parts.length >= 2) {
      const subdomain = parts[0];
      return subdomain !== APP_SUBDOMAIN && subdomain !== 'www' && subdomain !== 'localhost' && subdomain !== '127';
    }
    return false;
  }

  // É um subdomínio do imobpanda.site (domínio dos tenants)
  const parts = cleanHost.split('.');
  if (parts.length >= 2 && cleanHost.endsWith(TENANT_DOMAIN)) {
    const subdomain = parts[0];
    return subdomain !== 'www';
  }

  return false;
};

/**
 * Extrai o subdomínio de tenant da URL atual
 */
export const getTenantSubdomain = (): string | null => {
  if (!isTenantSubdomain()) {
    return null;
  }

  const hostname = window.location.hostname;
  const cleanHost = hostname.replace(/^www\./, '');
  const parts = cleanHost.split('.');

  return parts[0];
};

/**
 * Obtém a URL do app (login/admin)
 */
export const getAppUrl = (): string => {
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Desenvolvimento
  if (window.location.hostname.includes('localhost')) {
    return port ? `${protocol}//app.localhost:${port}` : `${protocol}//app.localhost`;
  }

  // Produção
  return `${protocol}//app.${MAIN_DOMAIN}`;
};

/**
 * Obtém a URL do domínio principal (landing page)
 */
export const getMainUrl = (): string => {
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Desenvolvimento
  if (window.location.hostname.includes('localhost')) {
    return port ? `${protocol}//localhost:${port}` : `${protocol}//localhost`;
  }

  // Produção
  return `${protocol}//${MAIN_DOMAIN}`;
};

/**
 * Redireciona para o app (login/admin)
 */
export const redirectToApp = (path: string = '/') => {
  const appUrl = getAppUrl();
  window.location.href = `${appUrl}${path}`;
};

/**
 * Redireciona para o domínio principal (landing page)
 */
export const redirectToMain = (path: string = '/') => {
  const mainUrl = getMainUrl();
  window.location.href = `${mainUrl}${path}`;
};

/**
 * Obtém a URL de um tenant específico
 */
export const getTenantUrl = (subdomain: string): string => {
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Validar subdomain
  if (!subdomain || subdomain === 'app' || subdomain === 'www') {
    console.warn('[domainDetection] Invalid tenant subdomain:', subdomain);
    return getMainUrl();
  }

  // Desenvolvimento
  if (window.location.hostname.includes('localhost')) {
    return port ? `${protocol}//${subdomain}.localhost:${port}` : `${protocol}//${subdomain}.localhost`;
  }

  // Produção - tenants usam imobpanda.site
  return `${protocol}//${subdomain}.${TENANT_DOMAIN}`;
};

/**
 * Redireciona para um tenant específico
 */
export const redirectToTenant = (subdomain: string, path: string = '/') => {
  const tenantUrl = getTenantUrl(subdomain);
  window.location.href = `${tenantUrl}${path}`;
};

/**
 * Tipo de domínio atual
 */
export type DomainType = 'main' | 'app' | 'tenant';

/**
 * Detecta o tipo de domínio atual
 */
export const getDomainType = (): DomainType => {
  // IMPORTANTE: Verificar app PRIMEIRO antes de tenant
  if (isAppSubdomain()) {
    return 'app';
  }

  if (isTenantSubdomain()) {
    return 'tenant';
  }

  return 'main';
};
